/**
 * Form View Component
 * ==================
 * 
 * Admin-only component for viewing and managing submitted form entries.
 * This component provides comprehensive functionality for viewing form data,
 * managing attachments, generating PDFs, sending emails, and navigating between entries.
 * 
 * Core Features:
 * - Form entry display with dynamic rendering
 * - Attachment viewing and management
 * - PDF generation and printing
 * - Email resending with custom recipients
 * - Navigation between form entries
 * - Responsive design for mobile and desktop
 * - Form metadata and field mapping
 * - WordPress field integration
 * 
 * Admin Capabilities:
 * - View complete form submissions
 * - Access all form attachments and signatures
 * - Generate and download PDFs
 * - Resend emails to standard or custom recipients
 * - Navigate to related form entries
 * - Create new forms of the same type
 * 
 * Technical Implementation:
 * - Reactive effects for form entry updates
 * - PDF caching for performance optimization
 * - Attachment blob categorization and management
 * - Modal management for attachments and actions
 * - Action sheet interfaces for mobile
 * - Popover interfaces for desktop
 * 
 * @author MacSafety Development Team
 * @version 1.0
 * @access Admin Only
 */

import { Component, OnInit, OnDestroy, effect, EffectRef, ViewChild, ElementRef } from '@angular/core';
import { FormService } from '../../../../services/form.service';
import { ActivatedRoute } from '@angular/router';
import { getFormModel } from '../../../../utils/form-model-map';
import { AttachmentWithUrl, ResolvedAttachments } from '../../../../models/forms/attachments/resolved-attachments';
import { Router } from '@angular/router';
import { FormStep } from '../../../../models/forms/form-steps.interface';
import { ScreenSizeService } from '../../../../services/screen-size.service';
import { ModalController, AlertController, ActionSheetController } from '@ionic/angular';
  import { FormRetrievalResponse, WordPressField } from '../../../../models/forms/form-retrieval-response';


import { FormRendererService, FormRenderData } from '../../../../services/form-render.service';
import { PrintPdfService } from '../../../../services/print-pdf.service';
import { EmailService } from '../../../../services/email.service';
import { ManualEmailRequest, ManualEmailHtmlRequest } from '../../../../models/email-request';
import { PdfGenerationRequest } from '../../../../models/print-pdf/print-pdf';

import { ModalAttachments } from '../../../../models/modal-viewer-attachments';
import { isPdf, isImage } from '../../../../utils/file-type';
import { FormFieldMetadata } from '../../../../utils/field-type-extractor';
import { FORM_REGISTRY } from '../../../../models/form-registry';


interface BlobCategorizationResult {
  pdfBlobNames: string[];
  imageBlobNames: string[];
}

/** How the resent email delivers the form: as a PDF attachment or rendered in the email body */
type EmailSendMode = 'pdf' | 'html';

@Component({
  selector: 'app-form-view',
  templateUrl: './form-view.component.html',
  styleUrls: ['./form-view.component.css']
})
export class FormViewComponent implements OnInit, OnDestroy {
  @ViewChild('formRenderContainer', { static: false }) formRenderContainer!: ElementRef;
  
  isAttachmentModalOpen: boolean = false;
  selectedAttachmentIndex: number = 0;
  identifier: string = '';
  formEntry: any = null;
  attachments: ResolvedAttachments = new ResolvedAttachments();
  modalAttachments: ModalAttachments[] = [];
  private formEntryEffectRef: EffectRef | undefined;
  formEntryId!: string;
  formType!: string;
  formTitle: string = '';
  fieldLabels: Record<string, string> = {};
  formSteps: FormStep[] = [];
  pdfDate: string = '';
  showPdfDropdown: boolean = false;
  isPdfCached: boolean = false;
  // Delivery mode chosen in the email dropdown; null shows the mode selection step
  selectedEmailMode: EmailSendMode | null = null;
  fieldMetadata: FormFieldMetadata = {};
  wordPressFields: WordPressField[] = [];
  // Expose service loading states
  get dataLoading() {
    return this.formService.dataLoading;
  }

  get pdfLoading() {
    return this.printPdfService.pdfLoading;
  }

  get emailLoading() {
    return this.emailService.emailLoading;
  }

  constructor(
    private formService: FormService,
    private route: ActivatedRoute,
    private _router: Router,
    private _screenSizeService: ScreenSizeService,
    private modalController: ModalController,
    private alertController: AlertController,
    private formRendererService: FormRendererService,
    private printPdfService: PrintPdfService,
    private emailService: EmailService,
    private actionSheetController: ActionSheetController
  ) {




    this.initializeFromRoute();
    this.setupFormEntryEffect();
  }

  /**
   * Lifecycle hook that is called after data-bound properties are initialized.
   * Component is already initialized from constructor, so this is minimal.
   */
  ngOnInit(): void {
    // Component is already initialized from constructor
  }

  /**
   * Lifecycle hook that is called when the component is destroyed.
   * Cleans up effects, clears service responses, and resets component state.
   */
  ngOnDestroy() {
    if (this.formEntryEffectRef) {
      this.formEntryEffectRef.destroy();
    }
    this.formService.clearFormEntryResponse();
    this.emailService.clearResults();
    this.formEntry = null;
  }

  /**
   * Initializes component state from route parameters.
   * Extracts the form entry ID and triggers the initial data fetch.
   * 
   * @private
   */
  private initializeFromRoute(): void {
    const id = this.route.snapshot.paramMap.get('id');
    
    if (id) {
      this.handleFormEntryIdChange(id);
    }
  }

  /**
   * Sets up the reactive effect to handle form entry response updates.
   * Uses Angular's effect system to automatically respond to service state changes.
   * 
   * @private
   */
  private setupFormEntryEffect(): void {
    this.formEntryEffectRef = effect(() => {
      const formEntryResponse = this.formService.formEntryResponse();
      if (formEntryResponse) {
        this.processFormEntryResponse(formEntryResponse);
        this.checkPdfCache(); // Check cache after processing form entry
      }
    });
  }

  /**
   * Handles changes to the form entry ID from route parameters.
   * Normalizes the form type, initializes metadata, and fetches the form entry.
   * 
   * @param id - The form entry ID from the route
   * @private
   */
  private handleFormEntryIdChange(id: string): void {
    this.formEntryId = id;
    const rawFormType = id.split('-')[0];
    const normalizedFormType = this.normalizeFormType(rawFormType);
    
    this.formType = normalizedFormType;
    this.initializeFormMetadata(normalizedFormType);
    this.fetchFormEntry(normalizedFormType, id);
  }

  /**
   * Checks if a PDF is cached for this form entry.
   * Uses localStorage to store PDF cache with a 2-day expiration.
   * Improves performance by avoiding regeneration of recently generated PDFs.
   * 
   * @private
   */
  private checkPdfCache(): void {
    try {
      const CACHE_PREFIX = 'pdf_cache_';
      const cacheKey = CACHE_PREFIX + this.formEntryId;
      const cached = localStorage.getItem(cacheKey);
      
      if (!cached) {
        this.isPdfCached = false;
        return;
      }

      const cachedData = JSON.parse(cached);
      const CACHE_DURATION = 2 * 24 * 60 * 60 * 1000; // 2 days
      const isExpired = (Date.now() - cachedData.timestamp) > CACHE_DURATION;
      
      this.isPdfCached = !isExpired;
      console.log(`PDF cached for ${this.formEntryId}: ${this.isPdfCached}`);
    } catch {
      this.isPdfCached = false;
    }
  }

  /**
   * Initializes form metadata (title, field labels, steps) based on form type.
   * Uses the form model class to extract form information and field mappings.
   * 
   * @param formType - The normalized form type identifier
   * @private
   */
  private initializeFormMetadata(formType: string): void {
    const ModelClass = getFormModel(formType);
    if (ModelClass) {
      const formInfo = ModelClass.getFormInfo();
      this.formTitle = formInfo.formTitle;
      this.fieldLabels = ModelClass.getFieldMappings();
      
      // Get form steps if available
      if (typeof ModelClass.getFormSteps === 'function') {
        this.formSteps = ModelClass.getFormSteps();
      } else if (formInfo.steps) {
        this.formSteps = formInfo.steps;
      } else {
        this.formSteps = [];
      }
    }
  }

  /**
   * Processes the form entry response and updates component state.
   * Extracts attachments, WordPress fields, and form entry data.
   * 
   * @param formEntryResponse - The response from the form service
   * @private
   */
  private processFormEntryResponse(formEntryResponse: FormRetrievalResponse<any>): void {
    if (!formEntryResponse) {
      this.formEntry = null;
      this.attachments = new ResolvedAttachments();
      return;
    }

    // Process attachments
    this.attachments = formEntryResponse.attachments;

    this.wordPressFields = formEntryResponse.formDoc.wordPressFields || [];

    // Process form entry data
    const rawEntry = formEntryResponse.formDoc.formEntryData;
    if (rawEntry && this.formType) {
      this.processFormEntryData(rawEntry);
    } else {
      this.formEntry = null;
    }
  }

  /**
   * Processes raw form entry data and converts it to the appropriate model.
   * Creates a model instance, extracts field metadata, and formats the PDF date.
   * 
   * @param rawEntry - The raw form entry data from the service
   * @private
   */
  private processFormEntryData(rawEntry: any): void {
    const ModelClass = getFormModel(this.formType);
    // Get field metadata from the registry using formType
    const registryEntry = FORM_REGISTRY[this.formType];
    this.fieldMetadata = registryEntry?.fieldMetadata || {};
    
    if (ModelClass) {
      const modelInstance = new ModelClass(rawEntry);
      this.formEntry = modelInstance;
      
      const formatDate = (dateString: string | Date): string => {
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
      
      this.pdfDate = formatDate(modelInstance.submissionDate);
      console.log('Form Entry processed:', this.formEntry);
    } else {
      this.formEntry = rawEntry;
    }
  }


  /**
   * Closes the attachment viewer modal.
   * Resets the modal state and clears selected attachment information.
   */
  closeAttachmentModal(): void {
    this.isAttachmentModalOpen = false;
  }
  

  /**
   * Resends email with standard configuration.
   * Uses the default recipients configured for the form type.
   *
   * @param mode - Delivery mode: 'pdf' attaches a PDF, 'html' sends the form in the email body
   * @async
   */
  async resendEmailStandard(mode: EmailSendMode = 'pdf') {
    const request = this.buildEmailRequest([]);
    await this.processEmailResend(request, mode);
  }

  /**
   * Resends email with custom recipients.
   * Allows admins to specify custom email addresses for the email.
   *
   * @param recipients - Array of custom email addresses
   * @param mode - Delivery mode: 'pdf' attaches a PDF, 'html' sends the form in the email body
   * @async
   */
  async resendEmailCustom(recipients: string[], mode: EmailSendMode = 'pdf') {
    const request = this.buildEmailRequest(recipients);
    await this.processEmailResend(request, mode);
  }

  /**
   * Builds the email request object with form data and recipient information.
   * Includes submission details and handles both standard and custom recipients.
   * 
   * @param customRecipients - Array of custom email addresses (empty for standard)
   * @returns ManualEmailRequest object ready for sending
   * @private
   */
  private buildEmailRequest(customRecipients: string[] = []): ManualEmailRequest {
    return {
      formType: this.formType,
      formId: this.formEntryId,
      recipients: customRecipients, // Empty array means use standard recipients
      emailTemplateData: {
        submittedByName: this.formEntry.submittedByName || 'Unknown',
        submittedByEmail: this.formEntry.submittedByEmail || '',
        submissionDate: this.pdfDate,
        specifiedRecipient: this.formEntry.recipientEmail || undefined
      }
    };
  }

  /**
   * Processes email resend with intelligent caching logic.
   * For PDF mode, only collects HTML and attachment data if PDF is not cached.
   * For HTML mode, always collects HTML content since it is sent in the email body.
   *
   * @param request - The email request object
   * @param mode - Delivery mode: 'pdf' attaches a PDF, 'html' sends the form in the email body
   * @private
   * @async
   */
  private async processEmailResend(request: ManualEmailRequest, mode: EmailSendMode = 'pdf') {
    if (mode === 'html') {
      await this.processEmailResendHtml(request);
      return;
    }

    let htmlContent = '';
    let attachmentData = {
      imageBlobNames: [] as string[],
      pdfBlobNames: [] as string[]
    };

    // Only collect HTML and attachment data if PDF is not cached
    if (!this.isPdfCached) {
      console.log('PDF not cached, collecting HTML and attachment data');

      const collected = this.collectHtmlAndAttachmentData();
      if (!collected) {
        return;
      }
      htmlContent = collected.htmlContent;
      attachmentData = collected.attachmentData;
    } else {
    }

    // Send email using the service
    await this.emailService.resendEmail(
      request,
      this.formEntryId,
      htmlContent,
      attachmentData,
      () => {
        console.log('Email resent successfully');
      },
      () => {
        console.log('Email resend failed');
      }
    );
  }

  /**
   * Sends the email with the form content rendered directly in the email body.
   * Always collects the current HTML content since no PDF (cached or generated) is used.
   *
   * @param request - The email request object
   * @private
   * @async
   */
  private async processEmailResendHtml(request: ManualEmailRequest) {
    const collected = this.collectHtmlAndAttachmentData();
    if (!collected) {
      return;
    }

    // The send-email-html endpoint does not take a form id
    const fullRequest: ManualEmailHtmlRequest = {
      formType: request.formType,
      recipients: request.recipients,
      emailTemplateData: request.emailTemplateData,
      htmlContent: collected.htmlContent,
      imageBlobNames: collected.attachmentData.imageBlobNames,
      pdfBlobNames: collected.attachmentData.pdfBlobNames
    };

    await this.emailService.sendEmailHtml(
      fullRequest,
      () => {
        console.log('Email with form content in body sent successfully');
      },
      () => {
        console.log('Email with form content in body failed');
      }
    );
  }

  /**
   * Collects the rendered form HTML and categorized attachment blob names.
   * Shared by the PDF-generation and HTML-in-body email paths.
   *
   * @returns Collected HTML content and attachment data, or null if no content is found
   * @private
   */
  private collectHtmlAndAttachmentData(): { htmlContent: string; attachmentData: { imageBlobNames: string[]; pdfBlobNames: string[] } } | null {
    const listContainer = document.querySelector('.scrollable-list-container');
    if (!listContainer || !listContainer.innerHTML.trim()) {
      console.error('No content found for email generation');
      return null;
    }

    const uploadBlobCategorizationResult = this.categorizeAttachmentBlobs(this.attachments.uploads);
    const signatureBlobCategorizationResult = this.categorizeAttachmentBlobs(this.attachments.signatures);

    return {
      htmlContent: listContainer.innerHTML,
      attachmentData: {
        imageBlobNames: [...uploadBlobCategorizationResult.imageBlobNames, ...signatureBlobCategorizationResult.imageBlobNames],
        pdfBlobNames: [...uploadBlobCategorizationResult.pdfBlobNames, ...signatureBlobCategorizationResult.pdfBlobNames]
      }
    };
  }

  

  /**
   * Opens an attachment modal for better viewing.
   * Sets up the modal with the selected attachment and its index.
   * 
   * @param event - Object containing field key, attachments, and selected index
   * @async
   */
  async openAttachmentModal(event: {fieldKey: string, attachments: ModalAttachments[], selectedIndex: number}): Promise<void> {
    this.modalAttachments = event.attachments;
    this.selectedAttachmentIndex = event.selectedIndex;
    this.isAttachmentModalOpen = true;
  }



  /**
   * Fetches the form entry data from the service.
   * Triggers the API call to retrieve the complete form entry.
   * 
   * @param formType - The type of form to fetch
   * @param id - The unique identifier of the form entry
   */
  fetchFormEntry(formType: string, id: string) {
    this.formService.fetchForm<any>(id);
  }

  /**
   * Normalizes form type strings for consistent processing.
   * Converts camelCase, spaces, and underscores to kebab-case format.
   * 
   * @param formType - The raw form type string
   * @returns Normalized form type string in kebab-case
   */
  normalizeFormType(formType: string): string {
    return formType
      .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
      .replace(/\s+/g, '-')
      .replace(/_/g, '-')
      .toLowerCase();
  }

  // Helper methods for going back
 

  /**
   * Creates a new form of the same type.
   * Navigates to the form creation page for the current form type.
   * 
   * @param formType - The type of form to create
   */
  createNew(formType: string): void {
    this._router.navigate(['/forms', formType]);
  }

  /**
   * Views all form entries of the same type.
   * Navigates to the form entries list filtered by form type.
   * 
   * @param formType - The form type to filter by
   */
  viewEntriesByType(formType: string): void {
    this._router.navigate(['/form-entries'], {
      queryParams: { formId: formType }
    });
  }

  /**
   * Views all form entries submitted by the same person.
   * Navigates to the form entries list filtered by submitter name.
   * 
   * @param submitterName - The name of the submitter to filter by
   */
  viewEntriesBySubmitter(submitterName: string): void {
    this._router.navigate(['/form-entries'], {
      queryParams: { submitter: submitterName }
    });
  }

  /**
   * Checks if the current device is mobile.
   * Uses the screen size service to determine device type.
   * 
   * @returns True if the device is mobile, false otherwise
   */
  isMobile(): boolean {
    const isMobile = this._screenSizeService.isMobileDevice();
    return isMobile;
  }

  /**
   * Prints the current form entry.
   * Extracts HTML content and generates a PDF for printing.
   * 
   * @async
   */
  async printEntry() {
    const request = this.preparePdfRequest();
    if (request) {
      this.printPdfService.generateAndPrintPdf(request, this.formEntryId);
    }
  }

  /**
   * Downloads the current form entry as a PDF.
   * Extracts HTML content and generates a downloadable PDF file.
   * 
   * @async
   */
  async downloadEntry() {
    const request = this.preparePdfRequest(true);
    if (request) {
      this.printPdfService.generateAndDownloadPdf(request, this.formEntryId);
    }
  }

  /**
   * Prepares the PDF generation request by extracting HTML and attachment data.
   * Categorizes attachments by type and formats the filename appropriately.
   * 
   * @param isDownload - Whether this is for download (affects filename formatting)
   * @returns PdfGenerationRequest object or null if content not found
   * @private
   */
  private preparePdfRequest(isDownload: boolean = false): PdfGenerationRequest | null {
    const listContainer = document.querySelector('.scrollable-list-container');
    
    if (!listContainer || !listContainer.innerHTML.trim()) {
      console.error('No content found in scrollable-list-container');
      return null;
    }

    const htmlContent = listContainer.innerHTML;

    
    const uploadBlobCategorizationResult = this.categorizeAttachmentBlobs(this.attachments.uploads);
    const signatureBlobCategorizationResult = this.categorizeAttachmentBlobs(this.attachments.signatures);
    const imageBlobNames = [...uploadBlobCategorizationResult.imageBlobNames, ...signatureBlobCategorizationResult.imageBlobNames];
    const pdfBlobNames = [...uploadBlobCategorizationResult.pdfBlobNames, ...signatureBlobCategorizationResult.pdfBlobNames];
    

    // Generate filename with optional formatting for downloads
    let pdfFileName = `MM_${this.formTitle}_${this.formEntry.submittedByName}_${this.pdfDate}.pdf`;
    if (isDownload) {
      pdfFileName = pdfFileName.replace(/[\s_]/g, '-');
    }

    return {
      formTitle: this.formTitle,
      htmlContent: htmlContent,
      imageBlobNames: imageBlobNames,
      pdfBlobNames: pdfBlobNames,
      pdfFileName: pdfFileName
    };
  }



  /**
   * Categorizes attachment blobs by file type.
   * Separates images and PDFs for proper PDF generation handling.
   * 
   * @param sections - Record of attachment sections with their blobs
   * @returns Object containing categorized blob names
   * @private
   */
 private categorizeAttachmentBlobs(
    sections: Record<string, AttachmentWithUrl[] | undefined>
  ): BlobCategorizationResult {
    const result: BlobCategorizationResult = {
      pdfBlobNames: [],
      imageBlobNames: []
    };
  
    Object.values(sections).forEach(attachments => {
      attachments?.forEach(({ blobName, contentType, fileName }) => {
        if (isImage(contentType, fileName)) {
          result.imageBlobNames.push(blobName);
        } else if (isPdf(contentType, fileName)) {
          result.pdfBlobNames.push(blobName);
        }
      });
    });
  
    return result;
  }


    /**
     * Presents navigation-focused action sheet for mobile devices.
     * Provides options to view entries by type or submitter.
     * 
     * @async
     */
    async presentNavigationSheet() {
      const actionSheet = await this.actionSheetController.create({
  
          header: 'Navigate',
          buttons: [
     
            {
              text: 'View All by Type',
              icon: 'albums-outline',
              handler: () => {
                this.viewEntriesByType(this.formType);
              }
            },
            {
              text: 'View All by Submitter',
              icon: 'person-outline',
              handler: () => {
                this.viewEntriesBySubmitter(this.formEntry.submittedByName);
              }
            },
            {
              text: 'Cancel',
              icon: 'close',
              role: 'cancel'
            }
          ]
        });
      
      await actionSheet.present();
    }
  
    /**
     * Presents action-focused action sheet for mobile devices.
     * Provides options for PDF actions, email actions, and form creation.
     * 
     * @async
     */
    async presentActionSheet() {
      const actionSheet = await this.actionSheetController.create({
      
          header: 'Actions',
          buttons: [
       
            {
              text: 'Download PDF',
              icon: 'download-outline',
              handler: () => this.downloadEntry(),
            
              disabled: this.pdfLoading().isLoading
            },
            {
              text: 'Resend Email',
              icon: 'mail-outline',
              handler: () => {
                if (!this.emailLoading().isLoading) {
                  this.presentEmailActionSheet();
                }
              },
              disabled: this.emailLoading().isLoading
            },
            {
              text: 'Create New',
              icon: 'add-circle-outline',
              handler: () => {
                this.createNew(this.formType);
              }
            },
            {
              text: 'Cancel',
              icon: 'close',
              role: 'cancel'
            }
          ]
      });
      
      await actionSheet.present();
    }

   

  /**
   * Presents email delivery mode options in an action sheet.
   * Allows admins to choose between sending the form as a PDF attachment
   * or rendered directly in the email body.
   *
   * @async
   */
  async presentEmailActionSheet() {
    const actionSheet = await this.actionSheetController.create({
      header: 'Email Options',
      buttons: [
        {
          text: 'Email with PDF',
          icon: 'document-attach-outline',
          handler: () => this.presentEmailRecipientsActionSheet('pdf')
        },
        {
          text: 'Email in Body',
          icon: 'mail-open-outline',
          handler: () => this.presentEmailRecipientsActionSheet('html')
        },
        {
          text: 'Back',
          icon: 'arrow-back-outline',
          role: 'cancel'
        }
      ]
    });

    await actionSheet.present();
  }

  /**
   * Presents email recipient options in an action sheet for the chosen delivery mode.
   * Allows admins to choose between standard and custom recipients.
   *
   * @param mode - Delivery mode: 'pdf' attaches a PDF, 'html' sends the form in the email body
   * @async
   */
  async presentEmailRecipientsActionSheet(mode: EmailSendMode) {
    const actionSheet = await this.actionSheetController.create({
      header: mode === 'pdf' ? 'Email with PDF' : 'Email in Body',
      buttons: [
        {
          text: 'Standard Recipients',
          icon: 'refresh-outline',
          handler: () => this.resendEmailStandard(mode)
        },
        {
          text: 'Custom Recipients',
          icon: 'people-outline',
          handler: () => this.presentCustomRecipientsAlert(mode)
        },
        {
          text: 'Back',
          icon: 'arrow-back-outline',
          role: 'cancel'
        }
      ]
    });

    await actionSheet.present();
  }

  /**
   * Presents custom recipients input alert.
   * Allows admins to enter custom email addresses separated by commas.
   * Validates input and handles errors gracefully.
   *
   * @param mode - Delivery mode: 'pdf' attaches a PDF, 'html' sends the form in the email body
   * @async
   */
  async presentCustomRecipientsAlert(mode: EmailSendMode = 'pdf') {
    const alert = await this.alertController.create({
      header: 'Custom Recipients',
      message: 'Enter email addresses separated by commas',
      inputs: [
        {
          name: 'recipients',
          type: 'textarea',
          placeholder: 'email1@example.com, email2@example.com',
          attributes: {
            rows: 3
          }
        }
      ],
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel'
        },
        {
          text: 'Send',
          handler: (data) => {
            if (data.recipients && data.recipients.trim()) {
              const recipients = data.recipients
                .split(',')
                .map((email: string) => email.trim())
                .filter((email: string) => email.length > 0);
              
              if (recipients.length > 0) {
                this.resendEmailCustom(recipients, mode);
                return true;
              }
            }
            
            // Show error for invalid input
            this.presentInvalidRecipientsAlert();
            return false;
          }
        }
      ]
    });

    await alert.present();
  }

  /**
   * Shows error alert for invalid recipients input.
   * Displays when no valid email addresses are provided.
   * 
   * @async
   */
  async presentInvalidRecipientsAlert() {
    const alert = await this.alertController.create({
      header: 'Invalid Recipients',
      message: 'Please enter at least one valid email address.',
      buttons: ['OK']
    });
    await alert.present();
  }
}